import { IMenuAddOn, IMenuItem } from 'entities/Menu/Menu.models';
import { ICartAddItemAddOnParams, ICartAddItemParams, ICartItemAddOnModel, ICartItemModel } from 'entities/Cart/Cart.models';
import { IOrderItemModel } from 'entities/Orders/Orders.models';

export const mapMenuAddonToCartAddItemAddonParams = (
  { menuItemId, price }: IMenuAddOn,
  quantity: number = 0,
): ICartAddItemAddOnParams => ({
  price,
  quantity,
  menuItemId,
});

export const mapMenuItemToCartAddItemParams = ({ menuItemId, price }: IMenuItem, quantity: number = 1): ICartAddItemParams => ({
  price,
  quantity,
  menuItemId,
  addOns: [],
  mealBuilderPicks: [],
});

export const mapCartItemAddonToCartAddItemAddonParams = ({
  quantity,
  menuItemId,
  price,
}: ICartItemAddOnModel): ICartAddItemAddOnParams => ({
  quantity,
  menuItemId,
  price,
});

export const mapCartItemToCartAddItemParams = (cartItem: ICartItemModel): ICartAddItemParams => ({
  ...cartItem,
  // Drop unavailable add-ons so a cart edit cannot re-submit them. Meal-builder picks are kept as-is:
  // MealBuilder resets optional ones whose section is gone and blocks update while a mandatory/selected
  // pick is unavailable (so the user re-picks or removes the item rather than silently losing it).
  addOns: (cartItem.addOns ?? []).filter((a) => a.isEnable).map(mapCartItemAddonToCartAddItemAddonParams),
  mealBuilderPicks: (cartItem.mealBuilderPicks ?? []).map((a) => ({
    menuItemId: a.menuItemId,
    quantity: a.quantity,
    role: a.mealBuilderRole,
    price: a.price,
  })),
});

export const mapOrderItemToCartAddItemParams = (orderItem: IOrderItemModel): ICartAddItemParams => ({
  price: orderItem.assetItemData.price,
  // Reorder always starts at quantity 1; the user bumps it in the modal (Bug 5).
  quantity: 1,
  menuItemId: orderItem.assetItemData.menuItemId,
  addOns: (orderItem.addOns ?? [])
    .filter((a) => a.assetItemData.isEnable)
    .map(({ assetItemData, quantity }) => ({
      menuItemId: assetItemData.menuItemId,
      quantity,
      price: assetItemData.price,
    })),
  mealBuilderPicks: (orderItem.mealBuilderPicks ?? [])
    .filter((p) => p.assetItemData.isEnable)
    .map((p) => {
      // The pick was charged as `pick.qty * parentQty * unitPrice`, where unitPrice is the RELATIVE
      // meal-builder price (mealBuilderPrice − base) for an included combo component — not the full
      // menu price. Recover that per-unit price from `total` so the reorder modal re-prices the pick
      // exactly as the menu does; `assetItemData.price` is the full menu price and would overcount (Bug 4).
      const divisor = p.quantity * orderItem.quantity;
      return {
        menuItemId: p.assetItemData.menuItemId,
        quantity: p.quantity,
        role: p.mealBuilderRole,
        price: divisor > 0 ? p.total / divisor : 0,
      };
    }),
});
